/**
 * IssuesProductRow — eine Produktzeile der Arbeitsliste:
 * Thumbnail, Name/SKU, Channel-Chips, Issue-Chips, Aktion "Beheben".
 */
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'
import { IssueChip } from './IssueChip'

interface IssuesProductRowProps {
  product: ProductWithIssues
  onFix: (product: ProductWithIssues) => void
}

export function IssuesProductRow({ product, onFix }: IssuesProductRowProps) {
  const { t } = useTranslation()
  const channels = product.channels ?? []

  return (
    <div
      data-testid={`issues-product-${product.product_id}`}
      className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 p-4"
    >
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800">
        {product.thumbnail_url && (
          <img src={product.thumbnail_url} alt="" className="h-full w-full object-cover" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-neutral-900 dark:text-neutral-100">
            {product.name ?? product.product_id}
          </span>
          {product.sku && (
            <span className="font-mono text-xs text-neutral-400">{product.sku}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {channels.map((c) => (
            <span
              key={c}
              className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
            >
              {c}
            </span>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {product.issues.map((iss, idx) => (
            <IssueChip key={`${product.product_id}-${idx}`} issue={iss} />
          ))}
        </div>
      </div>
      <Button
        size="sm"
        variant="secondary"
        data-testid={`issues-fix-${product.product_id}`}
        onClick={() => onFix(product)}
      >
        {t('productsIssues.workList.fix')}
      </Button>
    </div>
  )
}
